import React, { useRef, useState, useEffect } from 'react';
import { theme, cardStyle } from '../theme';

/**
 * SignaturePad — canvas-based signature pad rendered as a modal overlay.
 *
 * Props:
 *   onSave(base64Data) — called with canvas.toDataURL() when user clicks Save
 *   onClose()          — called when user dismisses the modal
 *   existingSignature  — optional base64 string to pre-display
 */
const SignaturePad = ({ onSave, onClose, existingSignature }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (existingSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasDrawn(true);
      };
      img.src = existingSignature;
    }
  }, [existingSignature]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    setHasDrawn(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a2332';
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDraw = () => {
    setIsDrawing(false);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSave = () => {
    if (!hasDrawn) return;
    const data = canvasRef.current.toDataURL('image/png');
    onSave(data);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 5000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ ...cardStyle, maxWidth: '520px', width: '90%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, color: theme.navy, fontSize: '1.05rem' }}>Signature</h3>
          <span onClick={onClose} style={{ cursor: 'pointer', fontSize: '1.4rem', color: theme.textMuted, fontWeight: '700' }}>x</span>
        </div>

        <canvas
          ref={canvasRef}
          width={480}
          height={200}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
          style={{
            width: '100%', height: '200px',
            border: `2px solid ${theme.border}`,
            borderRadius: theme.radiusSm,
            backgroundColor: '#fff',
            cursor: 'crosshair',
            touchAction: 'none',
          }}
        />

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button onClick={handleClear}
            style={{
              padding: '9px 20px', backgroundColor: '#6c757d', color: 'white',
              border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer', fontWeight: '600',
            }}>
            Clear
          </button>
          <button onClick={handleSave}
            disabled={!hasDrawn}
            style={{
              padding: '9px 24px', backgroundColor: theme.success, color: 'white',
              border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer', fontWeight: '700',
              opacity: hasDrawn ? 1 : 0.5,
            }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignaturePad;
